import React, { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Target, Calendar, ArrowUpRight } from 'lucide-react';
import './FinancialDashboard.css';

interface RevenueData {
  daily: { current: number; target: number; change: number };
  monthly: { current: number; target: number; change: number };
  annual: { current: number; target: number; change: number };
}

interface FinancialDashboardProps {
  revenue: RevenueData;
}

type Period = 'daily' | 'monthly' | 'annual';

const FinancialDashboard: React.FC<FinancialDashboardProps> = ({ revenue }) => {
  const [period, setPeriod] = useState<Period>('monthly');

  const currentData = revenue[period];
  const percentage = ((currentData.current / currentData.target) * 100);
  const isPositive = currentData.change >= 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPeriodLabel = (p: Period) => {
    switch (p) {
      case 'daily': return 'Today';
      case 'monthly': return 'This Month';
      case 'annual': return 'This Year';
    }
  };

  const getAllPeriodsData = () => [
    { key: 'daily' as Period, label: 'Daily', data: revenue.daily },
    { key: 'monthly' as Period, label: 'Monthly', data: revenue.monthly },
    { key: 'annual' as Period, label: 'Annual', data: revenue.annual },
  ];

  return (
    <div className="financial-dashboard">
      {/* Header with Toggle */}
      <div className="dashboard-header-row">
        <div className="dashboard-title">
          <Target className="title-icon" size={24} />
          <div>
            <h2>Mission Revenue</h2>
            <p>30k MRR Progress Dashboard</p>
          </div>
        </div>
        <div className="period-toggle">
          {getAllPeriodsData().map(({ key, label }) => (
            <button
              key={key}
              className={period === key ? 'active' : ''}
              onClick={() => setPeriod(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Cards Row - Full Width */}
      <div className="metrics-cards-row">
        {/* Current Revenue Card */}
        <div className="metric-card primary">
          <div className="card-header">
            <DollarSign size={20} />
            <span>Revenue {getPeriodLabel(period)}</span>
          </div>
          <div className="card-value">
            {formatCurrency(currentData.current)}
          </div>
          <div className={`card-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span>{isPositive ? '+' : ''}{currentData.change}% vs last {period}</span>
          </div>
        </div>

        {/* Target Card */}
        <div className="metric-card secondary">
          <div className="card-header">
            <Target size={20} />
            <span>Target {getPeriodLabel(period)}</span>
          </div>
          <div className="card-value">
            {formatCurrency(currentData.target)}
          </div>
          <div className="card-subtext">
            {formatCurrency(currentData.target - currentData.current)} remaining
          </div>
        </div>

        {/* Progress Card */}
        <div className="metric-card tertiary">
          <div className="card-header">
            <ArrowUpRight size={20} />
            <span>Progress to Goal</span>
          </div>
          <div className="progress-display">
            <div className="progress-circle">
              <svg viewBox="0 0 100 100">
                <circle className="progress-bg" cx="50" cy="50" r="42" />
                <circle 
                  className="progress-fill" 
                  cx="50" 
                  cy="50" 
                  r="42" 
                  style={{ strokeDasharray: `${percentage * 2.64} 264` }}
                />
              </svg>
              <div className="progress-text">
                <span className="percentage">{percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Time Card */}
        <div className="metric-card quaternary">
          <div className="card-header">
            <Calendar size={20} />
            <span>Timeline</span>
          </div>
          <div className="card-value">
            Q2 2026
          </div>
          <div className="card-subtext">
            Target completion date
          </div>
        </div>
      </div>

      {/* Bottom Stats Row */}
      <div className="stats-row">
        <div className="stat-item">
          <span className="stat-label">30k MRR Goal</span>
          <div className="stat-bar">
            <div 
              className="stat-fill" 
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <span className="stat-value">{percentage.toFixed(1)}% complete</span>
        </div>
        <div className="stat-summary">
          <span>At current pace: </span>
          <strong>{percentage >= 100 ? 'Goal achieved!' : `${(100 - percentage).toFixed(1)}% to go`}</strong>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
